/**
 * What can be *done* to one entity, declared once and rendered in both places
 * it is offered.
 *
 * An entity is acted on from two screens: its row in an `EntityTable`, and the
 * header of its detail page inside an `EntityDetail`. Those were two
 * implementations of the same set of acts — two sets of labels, two permission
 * checks that had to agree, two copies of each dialog — and, worse, the two
 * lists had **different members**. A user could be deactivated from its detail
 * page and not from its row, so "what can I do to this account?" had two answers
 * depending on which screen you were standing on.
 *
 * So the set is declared once, per entity, and this hook renders it into
 * whichever slot asked:
 *
 * ```tsx
 * const actions = useUserActions({ onChanged: () => users.reload() });
 *
 * <EntityTable actions={actions.rowActions} … />   // icons
 * {actions.dialogs}                                 // list screen mounts these
 *
 * <EntityDetail entity={actions.forRow(record)} … />  // text buttons + dialogs
 * ```
 *
 * The two screens cannot disagree about what exists, what it is called, who may
 * do it, when it is unavailable, or what it does — there is one of each.
 *
 * ## What belongs in the set, and what does not
 *
 * An action belongs here when it acts on **the entity**. It does not when it
 * acts on the **screen**:
 *
 * - `RoleDetailPage`'s Save and Discard operate on a permission draft that only
 *   exists while that page is open. There is no draft on a table row, so there
 *   is nothing to mirror.
 * - The assignee panel's "Assign", and a membership list's remove, act on a
 *   *relationship* shown in that panel, not on the record in the header.
 *
 * The test is whether the act would still mean the same thing with only a row
 * to work from. Rename, set password, deactivate: yes. Save a draft: no.
 *
 * ## Three shapes
 *
 * `form` opens a `FormDialog`, `confirm` a `ConfirmDialog`, `immediate` just
 * runs. Reactivating an account is `immediate` because a confirmation on a
 * reversible act trains people to click through confirmations — which is how
 * the one that mattered gets clicked through too.
 *
 * A `confirm` can **load** before it asks: deleting a group says how many
 * grants go with it, and those counts are fetched when the dialog opens. While
 * they load, or if they say the act cannot go ahead (`blocked`), Cancel works
 * and Confirm does not.
 */
import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContentText from '@mui/material/DialogContentText';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ErrorState } from '../components/ErrorState';
import { FormDialog } from '../components/FormDialog';
import type { EntityAffordances } from '../components/EntityDetail';
import type { EntityAction } from '../components/RowActions';
import { useAsync } from './useAsync';
import { useSubmit } from './useSubmit';
import { useAuth } from '../auth/useAuth';

interface BaseSpec<Row> {
  /** React key, and what a test names the action by. */
  key: string;
  /** The row tooltip *and* the header button's text. A specific verb phrase. */
  label: string;
  icon: ReactNode;
  /**
   * The permission it needs. Checked once, so a row and a header can never be
   * gated differently. Not enforcement — the server decides. See `auth/README.md`.
   */
  permission: string;
  /**
   * Whether this action exists for this row at all — deactivate on an account
   * that is already deactivated. Different from `lockedReason`: this one is not
   * applicable, rather than not allowed.
   */
  applies?: (row: Row) => boolean;
  /**
   * Why *this* row cannot take it right now, translated. Disables the action and
   * becomes its label, so the reader is told the reason.
   *
   * Deliberately not `hidden`: `hidden` is "the caller lacks the permission",
   * which `permission` already covers, and a row that silently has one fewer
   * button than the row above it teaches nobody anything.
   */
  lockedReason?: (row: Row) => string | null;
  /** `error` for anything destructive. Red icon, red confirm button. */
  tone?: 'default' | 'error';
  /** Drawn as the filled button on a detail header. At most one per entity. */
  primary?: boolean;
}

export interface FormActionSpec<Row, Form> extends BaseSpec<Row> {
  kind: 'form';
  /** Values a fresh dialog starts from, for a create dialog reusing `fields`. */
  blank: Form;
  formFor: (row: Row) => Form;
  /** A function, not a node, so a create dialog renders the identical inputs. */
  fields: (form: Form, patch: (changes: Partial<Form>) => void) => ReactNode;
  /** Under the fields. For what is true of editing and not of creating. */
  footer?: ReactNode;
  incomplete?: (form: Form) => boolean;
  title: (row: Row) => ReactNode;
  submitLabel?: string;
  maxWidth?: 'xs' | 'sm' | 'md';
  run: (row: Row, form: Form) => Promise<unknown>;
}

export interface ConfirmActionSpec<Row, Data = never> extends BaseSpec<Row> {
  kind: 'confirm';
  title: (row: Row) => string;
  /**
   * The consequence, in words. Not "this cannot be undone". A function when it
   * depends on the row, or on what `load` fetched.
   */
  message: ReactNode | ((row: Row, data: Data) => ReactNode);
  /**
   * Fetched when the dialog opens — what the act would take with it. Confirm
   * stays off until it arrives, and if it fails.
   */
  load?: (row: Row) => Promise<Data>;
  /**
   * Why the act cannot go ahead, given what `load` found — or null. Shown under
   * the message, and Confirm stays off. The server refuses the same case; this
   * only saves the round trip.
   */
  blocked?: (row: Row, data: Data) => string | null;
  confirmLabel?: string;
  run: (row: Row) => Promise<unknown>;
}

export interface ImmediateActionSpec<Row> extends BaseSpec<Row> {
  kind: 'immediate';
  run: (row: Row) => Promise<unknown>;
}

export type EntityActionSpec<Row> =
  | FormActionSpec<Row, never>
  | ConfirmActionSpec<Row, never>
  | ImmediateActionSpec<Row>;

/**
 * Declares a form action, erasing its `Form` type for the list.
 *
 * Each form action has its own shape, and a heterogeneous array cannot carry
 * them all. The type is checked here, at the declaration, and the hook then
 * holds the live values as `unknown` and hands them back through these same
 * closures — which still know what they are. The one cast is in this file
 * rather than at every declaration.
 */
export function formAction<Row, Form>(
  spec: FormActionSpec<Row, Form>,
): EntityActionSpec<Row> {
  return spec as unknown as EntityActionSpec<Row>;
}

/**
 * Declares a confirm action whose message depends on loaded data, erasing
 * `Data` for the list. The same trick as `formAction`, for the same reason.
 */
export function confirmAction<Row, Data>(
  spec: ConfirmActionSpec<Row, Data>,
): EntityActionSpec<Row> {
  return spec as unknown as EntityActionSpec<Row>;
}

export interface UseEntityActionsOptions {
  /**
   * Run after any action succeeds, with the value it resolved to. Reload the
   * list, or hand a fresh record over with `setData`.
   */
  onChanged: (result: unknown, key: string) => void;
}

export interface EntityActionsState<Row> {
  /** The icons, for an `EntityTable` row. */
  rowActions: (row: Row) => EntityAction[];
  /** Buttons and dialogs together, for `EntityDetail`'s `entity` prop. */
  forRow: (row: Row) => EntityAffordances;
  /** The dialogs alone. A list screen renders this once; see the folder README. */
  dialogs: ReactNode;
  /** True while any action is in flight. */
  pending: boolean;
  /**
   * The last failure, for a screen to render.
   *
   * A `form` or `confirm` action shows its own failure inside its dialog, where
   * the context is. An `immediate` one has no dialog, so without this its
   * failure would be silent — the act simply appears not to have happened. Pass
   * it to `EntityDetail`'s `error`, or render an `ErrorState` on a list screen.
   */
  error: Error | undefined;
}

export function useEntityActions<Row>(
  specs: readonly EntityActionSpec<Row>[],
  options: UseEntityActionsOptions,
): EntityActionsState<Row> {
  const { can } = useAuth();

  // One at a time. Two dialogs open at once is not a state any of these screens
  // can reach, and modelling it separately per action is how "close the edit
  // dialog and the confirm is still open behind it" ships.
  const [active, setActive] = useState<{ spec: EntityActionSpec<Row>; row: Row } | null>(null);
  const [form, setForm] = useState<unknown>(null);

  const run = useSubmit(async (spec: EntityActionSpec<Row>, row: Row, values: unknown) => {
    const result =
      spec.kind === 'form'
        ? await (spec as unknown as FormActionSpec<Row, unknown>).run(row, values)
        : await spec.run(row);
    options.onChanged(result, spec.key);
    return result ?? true;
  });

  // What an open confirm loaded. Tagged with its row, so a dialog never shows
  // the counts of the row opened before it while its own are still loading.
  const confirming =
    active?.spec.kind === 'confirm' ? (active.spec as unknown as ConfirmActionSpec<Row, unknown>) : null;
  const preview = useAsync(
    async () => {
      if (!active || !confirming?.load) return undefined;
      return { row: active.row, data: await confirming.load(active.row) };
    },
    [active],
    { enabled: confirming?.load !== undefined },
  );

  function start(spec: EntityActionSpec<Row>, row: Row) {
    if (spec.kind === 'immediate') {
      void run.submit(spec, row, null);
      return;
    }
    if (spec.kind === 'form') {
      setForm((spec as unknown as FormActionSpec<Row, unknown>).formFor(row));
    }
    setActive({ spec, row });
  }

  function close() {
    setActive(null);
    run.clearError();
  }

  /** The specs this row actually offers, with their disabled reason resolved. */
  function resolve(row: Row) {
    return specs
      .filter((spec) => can(spec.permission) && (spec.applies?.(row) ?? true))
      .map((spec) => ({ spec, locked: spec.lockedReason?.(row) ?? null }));
  }

  const rowActions = (row: Row): EntityAction[] =>
    resolve(row).map(({ spec, locked }) => ({
      key: spec.key,
      label: locked ?? spec.label,
      icon: spec.icon,
      onClick: () => start(spec, row),
      disabled: locked !== null || run.pending,
      ...(spec.tone === undefined ? {} : { tone: spec.tone }),
    }));

  const buttons = (row: Row): ReactNode =>
    resolve(row).map(({ spec, locked }) => (
      <Button
        key={spec.key}
        variant={spec.primary ? 'contained' : 'outlined'}
        color={spec.tone === 'error' ? 'error' : 'primary'}
        startIcon={spec.icon}
        disabled={locked !== null || run.pending}
        // `RowActions` needs a `<span>` so a disabled button's tooltip still
        // appears. Not needed here: the text is on the button, so a disabled one
        // still says what it is. What it cannot say is *why* — hence `title`.
        title={locked ?? undefined}
        onClick={() => start(spec, row)}
      >
        {spec.label}
      </Button>
    ));

  const dialogs =
    active === null ? null : confirming !== null ? (
      renderConfirmDialog(confirming, active.row)
    ) : (
      renderFormDialog(active.spec as unknown as FormActionSpec<Row, unknown>, active.row)
    );

  function renderConfirmDialog(spec: ConfirmActionSpec<Row, unknown>, row: Row) {
    const loaded =
      spec.load === undefined
        ? { data: undefined }
        : !preview.loading && preview.data?.row === row
          ? { data: preview.data.data }
          : null;
    const failed = spec.load !== undefined && !preview.loading ? preview.error : undefined;
    const blocked = loaded && spec.blocked ? spec.blocked(row, loaded.data) : null;

    const text = (node: ReactNode) =>
      typeof node === 'string' ? <DialogContentText>{node}</DialogContentText> : node;

    const message = failed ? (
      <ErrorState error={failed} severity="warning" />
    ) : loaded === null ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    ) : (
      <>
        {text(typeof spec.message === 'function' ? spec.message(row, loaded.data) : spec.message)}
        {blocked && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {blocked}
          </Alert>
        )}
      </>
    );

    return (
      <ConfirmDialog
        open
        title={spec.title(row)}
        message={message}
        {...(spec.confirmLabel === undefined ? {} : { confirmLabel: spec.confirmLabel })}
        tone={spec.tone === 'error' ? 'error' : 'primary'}
        pending={run.pending}
        confirmDisabled={loaded === null || failed !== undefined || blocked !== null}
        error={run.error}
        onClose={close}
        onConfirm={async () => {
          if (await run.submit(spec as unknown as EntityActionSpec<Row>, row, null)) close();
        }}
      />
    );
  }

  function renderFormDialog(spec: FormActionSpec<Row, unknown>, row: Row) {
    return (
      <FormDialog
        open
        title={spec.title(row)}
        {...(spec.submitLabel === undefined ? {} : { submitLabel: spec.submitLabel })}
        {...(spec.maxWidth === undefined ? {} : { maxWidth: spec.maxWidth })}
        pending={run.pending}
        error={run.error}
        disabled={spec.incomplete?.(form) ?? false}
        onClose={close}
        onSubmit={async () => {
          if (await run.submit(spec as unknown as EntityActionSpec<Row>, row, form)) close();
        }}
      >
        {spec.fields(form, (changes: Partial<unknown>) =>
          setForm((current: unknown) => ({ ...(current as object), ...(changes as object) })),
        )}
        {spec.footer}
      </FormDialog>
    );
  }

  return {
    rowActions,
    forRow: (row: Row) => ({ buttons: buttons(row), dialogs }),
    dialogs,
    pending: run.pending,
    error: run.error,
  };
}
